import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Calendar, Home, Mail, CheckCircle, Clock, Send, Upload, Sparkles, Bell,
  AlertCircle, XCircle, Loader2, Trash2, User, FileText, ChevronDown, ChevronUp, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import ComplianceChecklist from './ComplianceChecklist';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const EMPTY_PARTY = { name: '', email: '', phone: '' };
const EMPTY_FORM = {
  property_address: '',
  side: 'buyer',
  transaction_type: '',
  buyer: { ...EMPTY_PARTY },
  seller: { ...EMPTY_PARTY },
  lender: { ...EMPTY_PARTY },
  title_company: { ...EMPTY_PARTY },
  buyer_agent: { ...EMPTY_PARTY },
  listing_agent: { ...EMPTY_PARTY },
  closing_date: '',
  inspection_date: '',
  appraisal_date: '',
  earnest_money: '',
  purchase_price: '',
  notes: '',
  contract_pdf_path: '',
};

const SideBadge = ({ side }) => {
  if (!side) return null;
  const map = {
    buyer: 'bg-blue-100 text-blue-800 border-blue-300',
    seller: 'bg-orange-100 text-orange-800 border-orange-300',
    dual: 'bg-purple-100 text-purple-800 border-purple-300',
  };
  const cls = map[side];
  if (!cls) return null;
  return (
    <Badge className={`${cls} border capitalize`} data-testid={`tx-side-${side}`}>
      {side === 'dual' ? 'Dual Agency' : `${side} side`}
    </Badge>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    closed: 'bg-gray-200 text-gray-700 border-gray-300',
    cancelled: 'bg-red-100 text-red-700 border-red-300',
  };
  return (
    <Badge className={`${map[status] || map.active} border capitalize`} data-testid={`tx-status-${status}`}>
      {status}
    </Badge>
  );
};

// ---- Reusable slim party editor ----
const PartyFields = ({ label, value, onChange, required = false, testKey }) => (
  <div className="space-y-2 border border-gray-200 rounded-md p-3 bg-white">
    <Label className="text-sm font-semibold text-gray-800">
      {label}{required && <span className="text-red-500"> *</span>}
    </Label>
    <Input
      placeholder="Full name"
      value={value?.name || ''}
      onChange={(e) => onChange({ ...(value || {}), name: e.target.value })}
      data-testid={`party-name-${testKey}`}
    />
    <Input
      type="email"
      placeholder="email@example.com"
      value={value?.email || ''}
      onChange={(e) => onChange({ ...(value || {}), email: e.target.value })}
      data-testid={`party-email-${testKey}`}
    />
    <Input
      placeholder="Phone (optional)"
      value={value?.phone || ''}
      onChange={(e) => onChange({ ...(value || {}), phone: e.target.value })}
      data-testid={`party-phone-${testKey}`}
    />
  </div>
);

// ---- Reminders panel per transaction ----
const RemindersPanel = ({ transactionId, agentPhone }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    remind_at: '',
    channel: 'sms',
    recipient: 'self',
    custom_recipient: '',
    message: '',
    label: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/reminders?scope=all&transaction_id=${transactionId}`);
      setReminders(res.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [transactionId]);

  const submit = async () => {
    if (!form.remind_at) return toast.error('Pick a date/time');
    if (!form.message) return toast.error('Enter a reminder message');
    if (form.recipient === 'custom' && !form.custom_recipient) return toast.error('Enter the recipient');
    try {
      const payload = {
        transaction_id: transactionId,
        remind_at: new Date(form.remind_at).toISOString(),
        channel: form.channel,
        recipient: form.recipient === 'custom' ? form.custom_recipient : 'self',
        message: form.message,
        label: form.label || form.message.slice(0, 40),
      };
      await api.post('/reminders', payload);
      toast.success('Reminder scheduled');
      setShowAdd(false);
      setForm({ remind_at: '', channel: 'sms', recipient: 'self', custom_recipient: '', message: '', label: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create reminder');
    }
  };

  const cancelReminder = async (id) => {
    try {
      await api.delete(`/reminders/${id}`);
      toast.success('Reminder removed');
      load();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const now = new Date();

  return (
    <div className="mt-3 space-y-2" data-testid={`reminders-panel-${transactionId}`}>
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <Bell className="w-4 h-4 text-[#BCA480]" />
          Your Reminders {reminders.length > 0 && <span className="text-xs text-gray-500">({reminders.length})</span>}
        </h4>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid={`add-reminder-${transactionId}`}>
          {showAdd ? 'Close' : '+ Add Reminder'}
        </Button>
      </div>

      {showAdd && (
        <div className="border border-[#BCA480]/40 rounded p-3 bg-[#faf8f4] space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">When</Label>
              <Input
                type="datetime-local"
                value={form.remind_at}
                onChange={(e) => setForm(f => ({ ...f, remind_at: e.target.value }))}
                data-testid="new-reminder-when"
              />
            </div>
            <div>
              <Label className="text-xs">Channel</Label>
              <select
                className="w-full h-10 border border-gray-200 rounded px-3 text-sm"
                value={form.channel}
                onChange={(e) => setForm(f => ({ ...f, channel: e.target.value }))}
                data-testid="new-reminder-channel"
              >
                <option value="sms">SMS (text)</option>
                <option value="email">Email</option>
                <option value="portal">In portal only</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Send to</Label>
              <select
                className="w-full h-10 border border-gray-200 rounded px-3 text-sm"
                value={form.recipient}
                onChange={(e) => setForm(f => ({ ...f, recipient: e.target.value }))}
                data-testid="new-reminder-recipient"
              >
                <option value="self">Me{agentPhone && form.channel === 'sms' ? ` (${agentPhone})` : ''}</option>
                <option value="custom">Someone else…</option>
              </select>
            </div>
            {form.recipient === 'custom' && (
              <div>
                <Label className="text-xs">{form.channel === 'sms' ? 'Phone' : 'Email'}</Label>
                <Input
                  placeholder={form.channel === 'sms' ? '(901) 555-0000' : 'name@example.com'}
                  value={form.custom_recipient}
                  onChange={(e) => setForm(f => ({ ...f, custom_recipient: e.target.value }))}
                  data-testid="new-reminder-custom-recipient"
                />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={2}
              value={form.message}
              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="Follow up with buyer on inspection…"
              data-testid="new-reminder-message"
            />
          </div>
          <Button size="sm" onClick={submit} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="new-reminder-save">
            Schedule Reminder
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : reminders.length === 0 ? (
        <div className="text-xs text-gray-500 italic">No reminders yet. Add one to have Auri text or email you.</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {reminders.map(r => {
            const remindAt = new Date(r.remind_at);
            const sent = !!r.sent_at;
            const cancelled = !!r.cancelled_at;
            const upcoming = !sent && !cancelled && remindAt >= now;
            return (
              <li
                key={r.id}
                className={`flex items-center justify-between gap-2 py-1.5 px-2 rounded border ${
                  sent ? 'bg-emerald-50 border-emerald-200' : cancelled ? 'bg-gray-50 border-gray-200 opacity-70' : 'bg-white border-gray-200'
                }`}
                data-testid={`reminder-item-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{r.label || r.message.slice(0, 60)}</span>
                    <Badge variant="outline" className="text-[10px]">{r.channel}</Badge>
                    {sent && <span className="text-[10px] text-emerald-700 font-semibold">SENT</span>}
                    {cancelled && <span className="text-[10px] text-gray-500">CANCELLED</span>}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {remindAt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {r.recipient !== 'self' && ` → ${r.recipient}`}
                  </div>
                </div>
                {upcoming && (
                  <Button size="sm" variant="ghost" onClick={() => cancelReminder(r.id)} className="h-7 px-2 text-red-600" data-testid={`cancel-reminder-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ---- Transaction row / card ----
const TransactionCard = ({ tx, onRefresh, agentPhone, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [resending, setResending] = useState(false);

  const resendIntros = async () => {
    if (!window.confirm('Resend AURI intro emails to all parties on this transaction?')) return;
    setResending(true);
    try {
      await api.post(`/transactions/${tx.id}/resend-intros`);
      toast.success('Intro emails resent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend intro emails');
    } finally {
      setResending(false);
    }
  };

  return (
    <Card data-testid={`tx-card-${tx.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <SideBadge side={tx.side} />
              <StatusBadge status={tx.status} />
            </div>
            <h3 className="font-semibold text-gray-900 truncate" data-testid={`tx-address-${tx.id}`}>
              <Home className="w-4 h-4 inline mr-1.5 text-[#BCA480]" />
              {tx.property_address}
            </h3>
            <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
              {tx.closing_date && <span>📅 Close: {tx.closing_date}</span>}
              {tx.inspection_date && <span>🔍 Inspection: {tx.inspection_date}</span>}
              {tx.appraisal_date && <span>💰 Appraisal: {tx.appraisal_date}</span>}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            data-testid={`tx-expand-${tx.id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
            {tx.side !== 'seller' && tx.buyer?.name && (
              <div><User className="w-3.5 h-3.5 inline mr-1.5 text-blue-600" />
                <strong>Buyer:</strong> {tx.buyer.name}
                {tx.buyer.email && ` · ${tx.buyer.email}`}
                {tx.buyer.phone && ` · ${tx.buyer.phone}`}
              </div>
            )}
            {tx.side !== 'buyer' && tx.seller?.name && (
              <div><User className="w-3.5 h-3.5 inline mr-1.5 text-orange-600" />
                <strong>Seller:</strong> {tx.seller.name}
                {tx.seller.email && ` · ${tx.seller.email}`}
                {tx.seller.phone && ` · ${tx.seller.phone}`}
              </div>
            )}
            {tx.lender?.name && <div>🏦 <strong>Lender:</strong> {tx.lender.name}{tx.lender.email && ` · ${tx.lender.email}`}</div>}
            {tx.title_company?.name && <div>📜 <strong>Title:</strong> {tx.title_company.name}{tx.title_company.email && ` · ${tx.title_company.email}`}</div>}
            {tx.notes && <div className="text-xs text-gray-500 italic">Notes: {tx.notes}</div>}

            <ComplianceChecklist transactionId={tx.id} transactionType={tx.transaction_type} onChange={onRefresh} />

            <RemindersPanel transactionId={tx.id} agentPhone={agentPhone} />

            {tx.status !== 'cancelled' && (
              <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resendIntros}
                  disabled={resending}
                  className="h-8"
                  data-testid={`tx-resend-intros-${tx.id}`}
                >
                  {resending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                  Resend intro emails
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(tx)}
                  className="h-8 text-[#434345] border-[#BCA480]/60 hover:bg-[#faf8f4]"
                  data-testid={`tx-edit-${tx.id}`}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />
                  Edit transaction
                </Button>
                {tx.status === 'active' && <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm(`Cancel this transaction?\n\n${tx.property_address}\n\nThis will mark it as cancelled and stop all automated reminders. You can't undo this from the UI.`)) return;
                    try {
                      await api.delete(`/transactions/${tx.id}`);
                      toast.success('Transaction cancelled');
                      onRefresh && onRefresh();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Could not cancel transaction');
                    }
                  }}
                  className="h-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid={`tx-cancel-${tx.id}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />
                  Cancel transaction
                </Button>}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Main dashboard ----
const TransactionDashboard = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);

  // Contract upload state
  const [uploading, setUploading] = useState(false);
  const [suggestedReminders, setSuggestedReminders] = useState([]);
  const [pickedSuggestions, setPickedSuggestions] = useState({});
  const [showOptional, setShowOptional] = useState(false);
  const [transactionTypes, setTransactionTypes] = useState([]);

  const agentPhone = null; // The API returns tx.agent_phone but not the current user's phone here

  useEffect(() => { loadTransactions(); loadTransactionTypes(); }, []);

  const loadTransactionTypes = async () => {
    try {
      const res = await api.get('/compliance/transaction-types');
      setTransactionTypes(res.data?.transaction_types || []);
    } catch (e) {
      // silent
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/transactions');
      setTransactions(res.data || []);
    } catch (e) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (tx) => {
    const party = (p) => ({ name: p?.name || '', email: p?.email || '', phone: p?.phone || '' });
    const day = (d) => (d ? String(d).slice(0, 10) : '');
    setForm({
      ...EMPTY_FORM,
      property_address: tx.property_address || '',
      side: tx.side || 'buyer',
      transaction_type: tx.transaction_type || '',
      buyer: party(tx.buyer),
      seller: party(tx.seller),
      lender: party(tx.lender),
      title_company: party(tx.title_company),
      buyer_agent: party(tx.buyer_agent),
      listing_agent: party(tx.listing_agent),
      closing_date: day(tx.closing_date),
      inspection_date: day(tx.inspection_date),
      appraisal_date: day(tx.appraisal_date),
      earnest_money: tx.earnest_money ?? '',
      purchase_price: tx.purchase_price ?? '',
      notes: tx.notes || '',
      status: tx.status || 'active',
    });
    setSuggestedReminders([]);
    setPickedSuggestions({});
    setShowOptional(true);
    setEditingId(tx.id);
    setShowModal(true);
  };

  const openModal = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setSuggestedReminders([]);
    setPickedSuggestions({});
    setShowOptional(false);
    setShowModal(true);
  };

  const handleContractUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/transactions/extract-contract', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ex = res.data.extracted || {};
      // Prefill form
      setForm(f => ({
        ...f,
        property_address: ex.property_address || f.property_address,
        side: ex.side || f.side,
        buyer: {
          name: ex.buyer_name || f.buyer.name,
          email: ex.buyer_email || f.buyer.email,
          phone: ex.buyer_phone || f.buyer.phone,
        },
        seller: {
          name: ex.seller_name || f.seller.name,
          email: ex.seller_email || f.seller.email,
          phone: ex.seller_phone || f.seller.phone,
        },
        lender: {
          ...f.lender,
          name: ex.lender_name || f.lender.name,
          email: ex.lender_email || f.lender.email,
        },
        title_company: {
          ...f.title_company,
          name: ex.title_company_name || f.title_company.name,
          email: ex.title_company_email || f.title_company.email,
        },
        closing_date: ex.closing_date || f.closing_date,
        inspection_date: ex.inspection_date || f.inspection_date,
        appraisal_date: ex.appraisal_date || f.appraisal_date,
        earnest_money: ex.earnest_money_amount || f.earnest_money,
        purchase_price: ex.purchase_price || f.purchase_price,
        contract_pdf_path: res.data.contract_pdf_path || '',
      }));
      const suggestions = res.data.suggested_reminders || [];
      setSuggestedReminders(suggestions);
      // Pre-check all suggestions by default
      const picks = {};
      suggestions.forEach((_, i) => { picks[i] = true; });
      setPickedSuggestions(picks);
      toast.success(`Extracted ${Object.values(ex).filter(v => v).length} fields from contract`);
      if (suggestions.length) toast.info(`Auri suggests ${suggestions.length} reminders — review below`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to read PDF');
    } finally {
      setUploading(false);
      e.target.value = ''; // reset input
    }
  };

  const validate = () => {
    if (!form.property_address.trim()) return 'Property address is required';
    if (!form.side) return 'Pick which side you represent';
    if (form.side === 'buyer' && !form.buyer.name.trim()) return 'Buyer name is required';
    if (form.side === 'seller' && !form.seller.name.trim()) return 'Seller name is required';
    if (form.side === 'dual' && (!form.buyer.name.trim() || !form.seller.name.trim())) return 'Both buyer and seller names are required for dual agency';
    return null;
  };

  const cleanParty = (p) => {
    if (!p || (!p.name && !p.email && !p.phone)) return null;
    return { name: p.name || null, email: p.email || null, phone: p.phone || null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const payload = {
        property_address: form.property_address,
        side: form.side,
        transaction_type: form.transaction_type || null,
        buyer: cleanParty(form.buyer),
        seller: cleanParty(form.seller),
        lender: cleanParty(form.lender),
        title_company: cleanParty(form.title_company),
        buyer_agent: cleanParty(form.buyer_agent),
        listing_agent: cleanParty(form.listing_agent),
        closing_date: form.closing_date || null,
        inspection_date: form.inspection_date || null,
        appraisal_date: form.appraisal_date || null,
        earnest_money: form.earnest_money ? Number(form.earnest_money) : null,
        purchase_price: form.purchase_price ? Number(form.purchase_price) : null,
        contract_pdf_path: form.contract_pdf_path || null,
        notes: form.notes || null,
      };
      if (editingId) {
        delete payload.contract_pdf_path;
        payload.status = form.status;
        await api.patch(`/transactions/${editingId}`, payload);
        toast.success('Transaction updated');
        setShowModal(false);
        setEditingId(null);
        loadTransactions();
        return;
      }
      const res = await api.post('/transactions', payload);
      const txId = res.data.transaction?.id;

      // Save selected suggested reminders
      const picked = suggestedReminders.filter((_, i) => pickedSuggestions[i]);
      if (picked.length && txId) {
        await Promise.all(picked.map(r => api.post('/reminders', {
          transaction_id: txId,
          remind_at: r.remind_at,
          channel: r.channel,
          recipient: r.recipient,
          message: r.message,
          label: r.label,
        }).catch(() => null)));
      }

      toast.success(`Transaction created${picked.length ? ` · ${picked.length} reminders scheduled` : ''}`);
      setShowModal(false);
      loadTransactions();
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : `Failed to ${editingId ? 'update' : 'create'} transaction`);
    } finally {
      setSubmitting(false);
    }
  };

  const active = transactions.filter(t => t.status === 'active');
  const closed = transactions.filter(t => t.status !== 'active');

  return (
    <div className="space-y-6" data-testid="transaction-dashboard">
      <div className="flex justify-between items-start gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Transaction Coordinator</h2>
          <p className="text-sm text-gray-600 mt-1">
            Upload a contract, review the extracted info, and tell Auri exactly what to remind you about.
          </p>
        </div>
        <Button onClick={openModal} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="new-transaction-btn">
          <Plus className="w-4 h-4 mr-2" />New Transaction
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-emerald-700" data-testid="stat-active">{active.length}</div><div className="text-xs text-gray-500">Active</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-gray-600" data-testid="stat-closed">{closed.length}</div><div className="text-xs text-gray-500">Closed / Cancelled</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-[#BCA480]" data-testid="stat-total">{transactions.length}</div><div className="text-xs text-gray-500">Total</div></CardContent></Card>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading transactions…</div>
      ) : transactions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-700">No transactions yet.</p>
            <p className="text-xs mt-1">Click <strong>New Transaction</strong> — upload your Authentisign PDF and Auri will fill it in for you.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="transactions-list">
          {transactions.map((tx) => (
            <TransactionCard key={tx.id} tx={tx} onRefresh={loadTransactions} agentPhone={agentPhone} onEdit={openEditModal} />
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={editingId ? 'edit-transaction-modal' : 'new-transaction-modal'}>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Transaction' : 'New Transaction'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update any details that changed. Intro emails are not resent automatically.'
                : 'Upload your executed contract to auto-fill fields, or enter them manually.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {editingId && (
              <div>
                <Label>Status</Label>
                <select
                  className="w-full h-10 border border-gray-200 rounded px-3 text-sm mt-1.5"
                  value={form.status}
                  onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                  data-testid="form-status"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            )}

            {/* Contract upload */}
            {!editingId && <div className="border-2 border-dashed border-[#BCA480]/40 rounded-lg bg-[#faf8f4] p-4">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-[#BCA480] flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">Upload contract PDF for AI-powered auto-fill</p>
                  <p className="text-xs text-gray-600">Works with Authentisign, dotloop, DocuSign exports. Fields below prefill from the PDF.</p>
                </div>
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded cursor-pointer ${uploading ? 'bg-gray-300 text-gray-600' : 'bg-[#BCA480] hover:bg-[#a89470] text-white'}`}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Reading…' : 'Upload PDF'}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleContractUpload}
                    disabled={uploading}
                    data-testid="contract-upload-input"
                  />
                </label>
              </div>
              {form.contract_pdf_path && (
                <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Contract stored & fields prefilled — review below
                </p>
              )}
            </div>}

            {/* Side */}
            <div>
              <Label>Which side are you representing? <span className="text-red-500">*</span></Label>
              <div className="flex gap-2 mt-1.5">
                {['buyer', 'seller', 'dual'].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, side: s }))}
                    className={`px-4 py-2 rounded border text-sm capitalize font-medium ${
                      form.side === s
                        ? 'bg-[#434345] text-white border-[#434345]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#BCA480]'
                    }`}
                    data-testid={`side-select-${s}`}
                  >
                    {s === 'dual' ? 'Dual Agency' : `${s} side`}
                  </button>
                ))}
              </div>
            </div>

            {/* Transaction Type (drives compliance checklist) */}
            <div>
              <Label>Transaction Type <span className="text-gray-400 text-xs">(loads required-forms checklist)</span></Label>
              <select
                className="w-full h-10 border border-gray-200 rounded px-3 text-sm mt-1.5"
                value={form.transaction_type}
                onChange={(e) => setForm(f => ({ ...f, transaction_type: e.target.value }))}
                data-testid="form-transaction-type"
              >
                <option value="">Select transaction type…</option>
                {transactionTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Property */}
            <div>
              <Label>Property Address <span className="text-red-500">*</span></Label>
              <Input
                value={form.property_address}
                onChange={(e) => setForm(f => ({ ...f, property_address: e.target.value }))}
                placeholder="123 Main St, Olive Branch, MS 38654"
                data-testid="form-property-address"
              />
            </div>

            {/* Primary party (based on side) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(form.side === 'buyer' || form.side === 'dual') && (
                <PartyFields
                  label="Buyer"
                  value={form.buyer}
                  onChange={(v) => setForm(f => ({ ...f, buyer: v }))}
                  required
                  testKey="buyer"
                />
              )}
              {(form.side === 'seller' || form.side === 'dual') && (
                <PartyFields
                  label="Seller"
                  value={form.seller}
                  onChange={(v) => setForm(f => ({ ...f, seller: v }))}
                  required
                  testKey="seller"
                />
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Closing Date</Label>
                <Input
                  type="date"
                  value={form.closing_date}
                  onChange={(e) => setForm(f => ({ ...f, closing_date: e.target.value }))}
                  data-testid="form-closing-date"
                />
              </div>
              {(form.side === 'buyer' || form.side === 'dual') && (
                <div>
                  <Label>Inspection Date</Label>
                  <Input
                    type="date"
                    value={form.inspection_date}
                    onChange={(e) => setForm(f => ({ ...f, inspection_date: e.target.value }))}
                    data-testid="form-inspection-date"
                  />
                </div>
              )}
              <div>
                <Label>Appraisal Date</Label>
                <Input
                  type="date"
                  value={form.appraisal_date}
                  onChange={(e) => setForm(f => ({ ...f, appraisal_date: e.target.value }))}
                  data-testid="form-appraisal-date"
                />
              </div>
            </div>

            {/* Optional advanced */}
            <div>
              <button
                type="button"
                onClick={() => setShowOptional(!showOptional)}
                className="text-sm text-[#BCA480] hover:underline flex items-center gap-1"
                data-testid="toggle-optional"
              >
                {showOptional ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Optional: co-op agent, lender, title, notes
              </button>

              {showOptional && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(form.side === 'buyer' || form.side === 'dual') && (
                      <PartyFields
                        label={form.side === 'dual' ? 'Co-op Buyer Agent' : 'Listing Agent (other side)'}
                        value={form.listing_agent}
                        onChange={(v) => setForm(f => ({ ...f, listing_agent: v }))}
                        testKey="listing-agent"
                      />
                    )}
                    {(form.side === 'seller' || form.side === 'dual') && (
                      <PartyFields
                        label={form.side === 'dual' ? 'Co-op Listing Agent' : "Buyer's Agent (other side)"}
                        value={form.buyer_agent}
                        onChange={(v) => setForm(f => ({ ...f, buyer_agent: v }))}
                        testKey="buyer-agent"
                      />
                    )}
                    <PartyFields
                      label="Lender"
                      value={form.lender}
                      onChange={(v) => setForm(f => ({ ...f, lender: v }))}
                      testKey="lender"
                    />
                    <PartyFields
                      label="Title Company"
                      value={form.title_company}
                      onChange={(v) => setForm(f => ({ ...f, title_company: v }))}
                      testKey="title-company"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Purchase Price</Label>
                      <Input
                        type="number"
                        value={form.purchase_price}
                        onChange={(e) => setForm(f => ({ ...f, purchase_price: e.target.value }))}
                        placeholder="425000"
                        data-testid="form-purchase-price"
                      />
                    </div>
                    <div>
                      <Label>Earnest Money</Label>
                      <Input
                        type="number"
                        value={form.earnest_money}
                        onChange={(e) => setForm(f => ({ ...f, earnest_money: e.target.value }))}
                        placeholder="5000"
                        data-testid="form-earnest-money"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Any special instructions…"
                      data-testid="form-notes"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Suggested reminders */}
            {suggestedReminders.length > 0 && (
              <div className="border border-[#BCA480]/40 rounded-lg bg-[#faf8f4] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-[#BCA480]" />
                  <h4 className="text-sm font-semibold text-gray-800">Auri suggests these reminders — pick the ones you want:</h4>
                </div>
                <ul className="space-y-1.5">
                  {suggestedReminders.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!pickedSuggestions[i]}
                        onChange={(e) => setPickedSuggestions(p => ({ ...p, [i]: e.target.checked }))}
                        className="mt-1"
                        data-testid={`suggestion-check-${i}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{r.label}</div>
                        <div className="text-xs text-gray-500">{r.channel.toUpperCase()} · {r.message.slice(0, 80)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#BCA480] hover:bg-[#a89470] text-white"
                data-testid="submit-transaction-btn"
              >
                {submitting ? 'Saving…' : <><Send className="w-4 h-4 mr-2" />{editingId ? 'Save Changes' : 'Save Transaction'}</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionDashboard;
